/**
 * Action mapping. Gameplay code never reads raw keys — it reads named actions and axes.
 * Bindings use `KeyboardEvent.code` (layout independent) and standard-mapping gamepad indices.
 * Mouse buttons and the wheel are bound with pseudo-codes in the key lists: `Mouse0` (left),
 * `Mouse1` (middle), `Mouse2` (right), `WheelUp` / `WheelDown` (one press per notch). They only
 * count over the game canvas, so clicking a menu never fires a gun.
 */

export const BUTTON_ACTIONS = [
  'handbrake',
  /** Use: talk, open a place's menu, answer a phone; gets in a car only when nothing else is in reach. */
  'enterExit',
  /** Get in / out of a car, always (F, the touch CAR knob): never a phone box or a door instead. */
  'vehicle',
  'run',
  'attack',
  /** Hold to raise a firearm / lock onto the nearest target in front (weapons). */
  'aim',
  /** Cycle to the next carried weapon, fists included (weapons). */
  'weaponNext',
  'horn',
  'pause',
  'confirm',
  'map',
  'radio',
  'debug',
  'debugPhysics',
] as const;
export type ButtonAction = (typeof BUTTON_ACTIONS)[number];

/**
 * Axes, all in -1..1:
 *  moveX    on-foot screen-relative right (+) / left (-)
 *  moveY    on-foot screen-relative up (+) / down (-)
 *  throttle vehicle accelerate (+) / brake-reverse (-)
 *  steer    vehicle steer right (+) / left (-)
 */
export const AXES = ['moveX', 'moveY', 'throttle', 'steer'] as const;
export type AxisName = (typeof AXES)[number];

export interface KeyAxisBinding {
  positive: string[];
  negative: string[];
}

export interface GamepadAxisBinding {
  /** Stick axis index (standard mapping: 0 LX, 1 LY, 2 RX, 3 RY). */
  axis?: number;
  invert?: boolean;
  /** Analog triggers as buttons: value = buttons[positive].value - buttons[negative].value */
  positiveButton?: number;
  negativeButton?: number;
}

export interface Bindings {
  keys: Record<ButtonAction, string[]>;
  axes: Record<AxisName, KeyAxisBinding>;
  padButtons: Record<ButtonAction, number[]>;
  padAxes: Record<AxisName, GamepadAxisBinding[]>;
}

export function defaultBindings(): Bindings {
  return {
    keys: {
      handbrake: ['Space'],
      enterExit: ['KeyE', 'Enter'],
      vehicle: ['KeyF'],
      run: ['ShiftLeft', 'ShiftRight'],
      attack: ['KeyQ', 'ControlLeft', 'Mouse0'],
      // K for keyboard-only play (right hand), the right mouse button otherwise. Hold.
      aim: ['KeyK', 'Mouse2'],
      // 1 / ` and the mouse wheel. B J G T C V X Z Y N are the online hotkeys (src/online-ui).
      weaponNext: ['Digit1', 'Backquote', 'WheelDown', 'WheelUp'],
      horn: ['KeyH'],
      pause: ['Escape', 'KeyP'],
      confirm: ['Enter', 'Space'],
      map: ['KeyM', 'Tab'],
      radio: ['KeyR'],
      debug: ['F3'],
      debugPhysics: ['F4'],
    },
    axes: {
      moveX: { positive: ['KeyD', 'ArrowRight'], negative: ['KeyA', 'ArrowLeft'] },
      moveY: { positive: ['KeyW', 'ArrowUp'], negative: ['KeyS', 'ArrowDown'] },
      throttle: { positive: ['KeyW', 'ArrowUp'], negative: ['KeyS', 'ArrowDown'] },
      steer: { positive: ['KeyD', 'ArrowRight'], negative: ['KeyA', 'ArrowLeft'] },
    },
    padButtons: {
      handbrake: [0, 5],
      enterExit: [3],
      vehicle: [1],
      run: [0],
      // X, and RB on foot (RB is the handbrake in a car, where weapons are holstered).
      attack: [2, 5],
      // LT on foot (it brakes in a car) and LB.
      aim: [6, 4],
      // D-pad right (d-pad up is the radio).
      weaponNext: [15],
      horn: [10],
      pause: [9],
      confirm: [0],
      map: [8],
      radio: [12],
      debug: [],
      debugPhysics: [],
    },
    padAxes: {
      moveX: [{ axis: 0 }],
      moveY: [{ axis: 1, invert: true }],
      throttle: [{ positiveButton: 7, negativeButton: 6 }, { axis: 1, invert: true }],
      steer: [{ axis: 0 }],
    },
  };
}

/** Keys whose default browser behaviour (scrolling, focus) should be suppressed. */
export const PREVENT_DEFAULT_CODES = new Set([
  'Space',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'F3',
  'F4',
]);
